import { useAuthContext } from '@/lib/contexts/auth';
import { NotAuthorizedError, NotAuthenticatedError } from '@/lib/errors';
import { canAccess, type AccessContentPayload } from '@/lib/utils/access';

export type Status = 'granted' | 'unauthorized' | 'unauthenticated' | 'loading';

export const useCanAccess = (content: AccessContentPayload): Status => {
  const { isAuthenticated, getUserDataField, loading, userDataLoading } =
    useAuthContext();

  // content not present yet → don't decide
  if (!content) return 'loading';

  // auth not hydrated yet → don't decide
  if (loading) return 'loading';

  if (userDataLoading) return 'loading';

  const roles =
    getUserDataField(user => user.customer_groups?.map(g => g.name)) ?? [];

  const subscriptionTier =
    getUserDataField(user => user.subscription?.status) === 'active'
      ? 'basic'
      : undefined;

  let status: Status;
  try {
    canAccess({ isAuthenticated, roles, subscriptionTier }, content);
    status = 'granted';
  } catch (error) {
    if (error instanceof NotAuthorizedError) {
      status = 'unauthorized';
    } else if (error instanceof NotAuthenticatedError) {
      status = 'unauthenticated';
    } else {
      throw error;
    }
  }

  return status;
};
